import { type SizeSettingPreset, type SizeSettingSet, type SizeSettingSetMember } from '@air/api';

import { ALL_SOCIAL_MEDIA_FORMAT_IDS } from './socialMediaPresets';

/** One named width × height a workspace saved, reusable anywhere a size is picked. */
export type SavedSize = Pick<SizeSettingPreset, 'height' | 'id' | 'name' | 'width'>;

/** A named selection whose members retain their persisted insertion order when loaded. */
export type SizeSet = Pick<SizeSettingSet, 'id' | 'members' | 'name' | 'updatedAt'>;

/** Mirrors the backend's per-name cap on `SizeSettings.Presets`/`SizeSettings.Sets`, which `@air/api` does not expose as a constant. */
export const MAX_SAVED_SIZE_NAME_LENGTH = 255;

/** Mirrors the backend's cap on `SizeSettings.Sets` members, which `@air/api` does not expose as a constant. */
export const MAX_SIZE_SET_MEMBERS = 100;

/** Mirrors the backend's per-side dimension ceiling on `SizeSettings.Presets`, which `@air/api` does not expose as a constant. */
export const MAX_SAVED_SIZE_DIMENSION = 16000;

/** Generation is deliberately unbounded, so a size can be selectable and generatable without being savable -- this only gates the save/bookmark affordance. */
export const isSavableSize = ({ height, width }: Pick<SavedSize, 'height' | 'width'>): boolean =>
  Math.max(width, height) <= MAX_SAVED_SIZE_DIMENSION;

const SAVED_SIZE_SELECTION_ID_PREFIX = 'saved:';

/**
 * Namespaced clear of platform ids and the `custom_WxH` format id, since two differently-named
 * saved sizes can share dimensions -- keying selection by the format id would make clicking one
 * render both as selected.
 */
export const getSavedSizeSelectionId = (sizePresetId: SavedSize['id']): string =>
  `${SAVED_SIZE_SELECTION_ID_PREFIX}${sizePresetId}`;

export const isSavedSizeSelectionId = (selectionId: string): boolean =>
  selectionId.startsWith(SAVED_SIZE_SELECTION_ID_PREFIX);

export const getSizePresetIdFromSelectionId = (selectionId: string): string | undefined =>
  isSavedSizeSelectionId(selectionId) ? selectionId.slice(SAVED_SIZE_SELECTION_ID_PREFIX.length) : undefined;

/**
 * Smart resize understands arbitrary targets as `custom_WxH` ids — the only channel by which
 * pixel dimensions reach generation, export, and preview resolvers — so a saved size's payload
 * is derived into that shape only when it is actually sent, never used as its selection identity.
 */
export const getSizeFormatId = ({ height, width }: Pick<SavedSize, 'height' | 'width'>): string =>
  `custom_${width}x${height}`;

const SIZE_FORMAT_ID_PATTERN = /^custom_(\d+)x(\d+)$/;

/** No ceiling here -- generation is unbounded and `isSavableSize` gates saving; this rejects only
 * what describes no image at all: a zero side, or digits past safe-integer precision. */
const isRealDimension = (value: number): boolean => Number.isSafeInteger(value) && value > 0;

/** Reverses `getSizeFormatId`; null for a platform key, and for digits that describe no real size
 * (`custom_0x800`), so a meaningless shape cannot travel on as if it were one. */
export const parseSizeFormatId = (formatId: string): Pick<SavedSize, 'height' | 'width'> | null => {
  const match = SIZE_FORMAT_ID_PATTERN.exec(formatId);

  if (!match?.[1] || !match[2]) {
    return null;
  }

  const width = Number(match[1]);
  const height = Number(match[2]);

  if (!isRealDimension(width) || !isRealDimension(height)) {
    return null;
  }

  return { height, width };
};

export const getSizeDimensionsLabel = ({ height, width }: Pick<SavedSize, 'height' | 'width'>): string =>
  `${width} × ${height}`;

/**
 * Whether two names are the same thing to the person who typed them, matching the workspace's
 * case-insensitive uniqueness constraint on saved size and set names.
 */
export const matchesSavedName = (name: string, otherName: string): boolean =>
  name.trim().toLowerCase() === otherName.trim().toLowerCase();

/** A name distinct from `existingNames`, matched case/whitespace-insensitively, by appending an incrementing numbered suffix. */
export const getUniqueSuffixedName = (baseName: string, existingNames: string[]): string => {
  const takenNames = new Set(existingNames.map((existingName) => existingName.trim().toLowerCase()));

  if (!takenNames.has(baseName.toLowerCase())) {
    return baseName;
  }

  let suffix = 2;

  while (takenNames.has(`${baseName} (${suffix})`.toLowerCase())) {
    suffix += 1;
  }

  return `${baseName} (${suffix})`;
};

/**
 * Default name for a session size promoted to a saved size while saving a set, so the only input
 * a set-save requires is the set's own name.
 */
export const getDefaultSizePresetName = (
  { height, width }: Pick<SavedSize, 'height' | 'width'>,
  existingNames: string[],
): string => getUniqueSuffixedName(getSizeDimensionsLabel({ height, width }), existingNames);

export type RetryCreateOnNameConflictParams<T> = {
  baseName: string;
  create: (name: string) => Promise<T>;
  existingNames: string[];
  isConflictError: (error: unknown) => boolean;
  /** Bounds the retry so a persistently stale `existingNames` list can't loop forever. */
  maxRetries?: number;
};

/**
 * Retries with the next numbered suffix when `create` reports a conflict, since `existingNames`
 * can be stale against a concurrent create landing on the same default name. A non-conflict
 * failure (or an exhausted retry budget) rethrows rather than retrying forever.
 */
export const retryCreateOnNameConflict = async <T>({
  baseName,
  create,
  existingNames,
  isConflictError,
  maxRetries = 3,
}: RetryCreateOnNameConflictParams<T>): Promise<T> => {
  const attemptedNames: string[] = [];

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    const candidateName = getUniqueSuffixedName(baseName, [...existingNames, ...attemptedNames]);

    try {
      return await create(candidateName);
    } catch (error) {
      if (!isConflictError(error) || attempt === maxRetries) {
        throw error;
      }

      attemptedNames.push(candidateName);
    }
  }

  // Unreachable: the loop above always returns or throws.
  throw new Error('retryCreateOnNameConflict exited without a result');
};

export type ResolveSizeSetSelectionIdsParams = {
  /** Every platform key this surface can select — a video picker can't take every image format. */
  selectablePlatformKeys: Set<string>;
  savedSizesById: Map<SavedSize['id'], SavedSize>;
};

/**
 * Drops whatever this surface can't resolve -- a deleted `sizePresetId`, or a `platformKey` this
 * surface doesn't offer -- so the set stays loadable with whatever remains rather than being
 * hidden outright.
 */
export const resolveSizeSetSelectionIds = (
  members: SizeSettingSetMember[],
  { savedSizesById, selectablePlatformKeys }: ResolveSizeSetSelectionIdsParams,
): string[] => {
  const selectionIds: string[] = [];

  for (const member of members) {
    if (member.type === 'sizePreset') {
      if (savedSizesById.has(member.sizePresetId)) {
        selectionIds.push(getSavedSizeSelectionId(member.sizePresetId));
      }

      continue;
    }

    if (selectablePlatformKeys.has(member.platformKey)) {
      selectionIds.push(member.platformKey);
    }
  }

  return selectionIds;
};

/**
 * The chip renders selected only when every member already is, so clicking a fully-selected set
 * clears its members; a partially- or un-selected set fills in what's missing instead.
 */
export const toggleSetSelectionIds = (selectedFormatIds: Set<string>, selectionIds: string[]): Set<string> => {
  const next = new Set(selectedFormatIds);
  const isFullySelected = selectionIds.every((id) => next.has(id));

  for (const id of selectionIds) {
    if (isFullySelected) {
      next.delete(id);
    } else {
      next.add(id);
    }
  }

  return next;
};

/**
 * Keeps the replacement at the same position in iteration order -- a plain delete+add would move
 * it to the end, and insertion order is what `handleSaveSet` persists. Returns the same reference
 * when `fromId` isn't selected, since callers feed this to state setters that skip a re-render on
 * an unchanged reference.
 */
export const replaceSelectionId = (selectedFormatIds: Set<string>, fromId: string, toId: string): Set<string> => {
  if (!selectedFormatIds.has(fromId)) {
    return selectedFormatIds;
  }

  const next = new Set<string>();

  for (const id of selectedFormatIds) {
    next.add(id === fromId ? toId : id);
  }

  return next;
};

export type UnsavedCustomSizeInput = {
  height: number;
  /** The session-local `custom_WxH` selection id, so a caller can map the result back to it. */
  id: string;
  width: number;
};

export type SaveSetMemberInput = SizeSettingSetMember | { customSize: UnsavedCustomSizeInput; type: 'customSize' };

/**
 * Keeps generation order, and drops an id it cannot classify: `smartResizeFormatId` is an arbitrary
 * string at the tool boundary, and an unrecognized key persists as a member nothing can resolve.
 */
export const toSaveSetMembers = (
  formatIds: string[],
  savedSizesById: Map<SavedSize['id'], SavedSize>,
): SaveSetMemberInput[] => {
  const savedSizeByFormatId = new Map(
    [...savedSizesById.values()].map((sizePreset) => [getSizeFormatId(sizePreset), sizePreset]),
  );

  return formatIds.flatMap((formatId): SaveSetMemberInput[] => {
    const savedSize = savedSizeByFormatId.get(formatId);

    if (savedSize) {
      return [{ sizePresetId: savedSize.id, type: 'sizePreset' }];
    }

    const parsed = parseSizeFormatId(formatId);

    if (parsed) {
      return [{ customSize: { ...parsed, id: formatId }, type: 'customSize' }];
    }

    if (ALL_SOCIAL_MEDIA_FORMAT_IDS.has(formatId)) {
      return [{ platformKey: formatId, type: 'platform' }];
    }

    return [];
  });
};
